import { useRef, useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { TextField, Dropdown, Button, Typography, InlineAlert } from '@nwh-consumer-digital/nw-component-lib'
import { Grid, Box, Paper } from '@mui/material'
import styled from '@emotion/styled'
import { useProgress } from '../context/ProgressContext'

const Item = styled(Paper)(() => ({
  textAlign: 'left',
  maxWidth: '400px',
  marginBottom: '18px', // There is an extra 6 pixels this makes 24px
}))

const adultPageOne = (props) => {
  const { SubmissionState, onFormStateChange } = props
  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    formState: { errors },
  } = useForm({ mode: 'onBlur' })
  //UseProgress Context Hook
  const { updateProgress } = useProgress()

  useEffect(() => {
    updateProgress(25)
    if (SubmissionState.sectionOneForm) {
      setValue('firstName', SubmissionState.sectionOneForm?.firstName)
      setValue('lastName', SubmissionState.sectionOneForm?.lastName)
      setValue('mInitial', SubmissionState.sectionOneForm?.mInitial)
      setValue('DOBChild', SubmissionState.sectionOneForm?.DOBChild)
      setValue('gender', SubmissionState.sectionOneForm?.gender)
      setValue('streetNumber', SubmissionState.sectionOneForm?.streetNumber)
      setValue('City', SubmissionState.sectionOneForm?.City)
      setValue('State', SubmissionState.sectionOneForm?.State)
      setValue('Zip', SubmissionState.sectionOneForm?.Zip)
      setValue('relationshiptoPatient', SubmissionState.sectionOneForm?.relationshiptoPatient)
    }
  }, [])

  // Count the number of errors on the page and, if any are found, scroll to the reference above the inline alert box after tallying them.
  const errorCount = Object.keys(errors).length
  const targetRef = useRef(null)
  const scrollToTarget = () => {
    if (targetRef.current) {
      targetRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }
  useEffect(() => {
    if (errorCount > 0) {
      scrollToTarget()
    }
  }, [errors])

  const handleRegistration = (data) => {
    SubmissionState.sectionOneForm = data
    SubmissionState.goBackPageTwo = true
    onFormStateChange()
  }

  return (
    <Box>
      <Grid container direction="column" justifyContent="space-between" alignItems="left">
        <Grid item xs={12}>
          <Typography
            variant="h1"
            sx={{
              marginTop: {
                xs: '1.25rem',
                sm: '1.25rem',
                md: '1.25rem',
                lg: '5rem',
                xl: '5rem',
              },
              marginBottom: {
                xs: '2rem',
                sm: '2rem',
                md: '2rem',
                lg: '2rem',
                xl: '2rem',
              },
            }}>
            Adult patient's information
          </Typography>
        </Grid>
        <Grid
          item
          xs={12}
          style={{
            maxWidth: window.innerWidth <= 768 ? '400px' : 'none',
            marginBottom: 24,
            textAlign: 'justify',
            lineHeight: 1.6,
            wordSpacing: '0.05em',
          }}>
          <Typography type="body" size="large" weight="light">
            {' '}
            Enter the adult patient's information details below whom you are requesting proxy access for.
          </Typography>
        </Grid>
        <form onSubmit={handleSubmit(handleRegistration)}>
          <Grid item xs={12} className="requiredSpacing">
            <Item elevation={0}>
              <label ref={targetRef} className="required">
                <span className="red">*</span> Required
              </label>
            </Item>
          </Grid>
          {errorCount > 0 && (
            <Grid item xs={12}>
              <Item elevation={0}>
                <InlineAlert severity="error" fullWidth text={`There are ${errorCount} errors on this page.`} />
              </Item>
            </Grid>
          )}
          <Grid item xs={12}>
            <Item elevation={0}>
              <Controller
                name="firstName"
                control={control}
                rules={{
                  required: true,
                  pattern: /^[A-Z a-z]*$/i,
                }}
                render={({ field: { onChange } }) => (
                  <TextField
                    id="firstName"
                    onChange={onChange}
                    defaultValue={SubmissionState.sectionOneForm?.firstName}
                    label={
                      <span>
                        First Name <span className="red">*</span>
                      </span>
                    }
                    fullWidth
                    error={Boolean(errors?.firstName)}
                    helperText={
                      errors?.firstName
                        ? errors?.firstName?.type === 'required'
                          ? 'First name is required.'
                          : errors?.firstName?.type === 'pattern'
                          ? 'First name contains invalid characters.'
                          : 'First name is required.'
                        : ''
                    }
                  />
                )}
              />
            </Item>
          </Grid>
          <Grid item xs={12}>
            <Item elevation={0}>
              <Controller
                name="lastName"
                control={control}
                rules={{
                  required: true,
                  pattern: /^[A-Z a-z]*$/i,
                }}
                render={({ field: { onChange } }) => (
                  <TextField
                    id="lastName"
                    defaultValue={SubmissionState.sectionOneForm?.lastName}
                    onChange={onChange}
                    label={
                      <span>
                        Last Name <span className="red">*</span>
                      </span>
                    }
                    fullWidth
                    error={Boolean(errors?.lastName)}
                    helperText={
                      errors?.lastName
                        ? errors?.lastName?.type === 'required'
                          ? 'Last name is required.'
                          : errors?.lastName?.type === 'pattern'
                          ? 'Last name contains invalid characters.'
                          : 'Last name is required.'
                        : ''
                    }
                  />
                )}
              />
            </Item>
          </Grid>
          <Grid item xs={12}>
            <Item elevation={0}>
              <Controller
                name="mInitial"
                control={control}
                rules={{
                  pattern: {
                    value: /^[A-Za-z]+$/,
                    message: 'Please use alphabetic characters only.',
                  },
                }}
                render={({ field: { onChange, value } }) => (
                  <TextField
                    id="mInitial"
                    onChange={onChange}
                    value={SubmissionState.sectionOneForm?.mInitial}
                    label="Middle initial"
                    fullWidth
                    error={Boolean(errors?.mInitial)}
                    helperText={errors?.mInitial?.message || ''}
                  />
                )}
              />
            </Item>
          </Grid>
          <Grid item xs={12}>
            <Item elevation={0}>
              <Controller
                name="DOBChild"
                control={control}
                rules={{
                  pattern:
                    /^(0[1-9]|1[0-2])\/(0[1-9]|[1-2][0-9]|3[0-1])\/(19\d{2}|20[0-2]\d|20[2-9][0-9]|20[3-9][0-9]|21\d{2})$/,
                  required: true,
                }}
                render={({ field: { onChange } }) => (
                  <TextField
                    id="DOBChild"
                    defaultValue={SubmissionState.sectionOneForm?.DOBChild}
                    onChange={onChange}
                    label={
                      <span>
                        Date of birth (MM/DD/YYYY) <span className="red">*</span>
                      </span>
                    }
                    fullWidth
                    error={Boolean(errors?.DOBChild)}
                    helperText={
                      errors?.DOBChild
                        ? errors?.DOBChild?.type === 'required'
                          ? 'Date of Birth is required.'
                          : errors?.DOBChild?.type === 'pattern'
                          ? 'Please use numeric characters only, and correct Month/Date/Year values.'
                          : 'Date of Birth is required.'
                        : ''
                    }
                  />
                )}
              />
            </Item>
          </Grid>
          <Grid item xs={12}>
            <Item elevation={0}>
              <Controller
                name="gender"
                control={control}
                rules={{ required: true }}
                render={({ field: { onChange, value } }) => (
                  <Dropdown
                    id="gender"
                    label={
                      <span>
                        Birth gender <span className="red">*</span>
                      </span>
                    }
                    value={value}
                    onChange={onChange}
                    error={Boolean(errors?.gender)}
                    helperText={errors?.gender ? 'Birth gender is required.' : undefined}
                    selectOptions={[
                      { name: 'Female', value: 'Female' },
                      { name: 'Male', value: 'Male' },
                      { name: 'Other', value: 'Other' },
                    ]}
                  />
                )}
              />
            </Item>
          </Grid>
          <Grid item xs={12}>
            <Item elevation={0}>
              <Controller
                name="streetNumber"
                control={control}
                rules={{
                  required: true,
                  pattern: /^[A-Za-z0-9 -]+$/,
                }}
                render={({ field: { onChange } }) => (
                  <TextField
                    id="streetNumber"
                    defaultValue={SubmissionState.sectionOneForm?.streetNumber}
                    onChange={onChange}
                    label={
                      <span>
                        Address <span className="red">*</span>
                      </span>
                    }
                    fullWidth
                    error={Boolean(errors?.streetNumber)}
                    helperText={
                      errors?.streetNumber
                        ? errors?.streetNumber?.type === 'required'
                          ? 'Address is required.'
                          : errors?.streetNumber?.type === 'pattern'
                          ? 'Please use alphabetic characters only.'
                          : 'Address is required.'
                        : ''
                    }
                  />
                )}
              />
            </Item>
          </Grid>
          <Grid item xs={12}>
            <Item elevation={0}>
              <Controller
                name="City"
                control={control}
                rules={{
                  required: true,
                  pattern: /^[A-Z a-z]*$/i,
                }}
                render={({ field: { onChange } }) => (
                  <TextField
                    id="City"
                    defaultValue={SubmissionState.sectionOneForm?.City}
                    onChange={onChange}
                    label={
                      <span>
                        City <span className="red">*</span>
                      </span>
                    }
                    fullWidth
                    error={Boolean(errors?.City)}
                    helperText={
                      errors?.City
                        ? errors?.City?.type === 'required'
                          ? 'City is required.'
                          : errors?.City?.type === 'pattern'
                          ? 'Please use alphabetic characters only.'
                          : 'City is required.'
                        : ''
                    }
                  />
                )}
              />
            </Item>
          </Grid>
          <Grid item xs={12}>
            <Item elevation={0}>
              <Controller
                name="State"
                rules={{
                  required: true,
                  pattern: /^[A-Z a-z]*$/i,
                }}
                control={control}
                render={({ field: { onChange } }) => (
                  <TextField
                    id="State"
                    defaultValue={SubmissionState.sectionOneForm?.State}
                    onChange={onChange}
                    label={
                      <span>
                        State <span className="red">*</span>
                      </span>
                    }
                    fullWidth
                    error={Boolean(errors?.State)}
                    helperText={
                      errors?.State
                        ? errors?.State?.type === 'required'
                          ? 'State is required.'
                          : errors?.State?.type === 'pattern'
                          ? 'Please use alphabetic characters only.'
                          : 'State is required.'
                        : ''
                    }
                  />
                )}
              />
            </Item>
          </Grid>
          <Grid item xs={12}>
            <Item elevation={0}>
              <Controller
                name="Zip"
                rules={{
                  required: true,
                  pattern: /(^\d{5}$)|(^\d{5}-\d{4}$)/,
                }}
                control={control}
                render={({ field: { onChange } }) => (
                  <TextField
                    id="Zip"
                    onChange={onChange}
                    defaultValue={SubmissionState.sectionOneForm?.Zip}
                    label={
                      <span>
                        Zip <span className="red">*</span>
                      </span>
                    }
                    error={errors?.Zip}
                    fullWidth
                    helperText={
                      errors?.Zip
                        ? errors?.Zip?.type === 'required'
                          ? 'Zipcode is required.'
                          : errors?.Zip?.type === 'pattern'
                          ? 'Please use 5 digits of numeric characters only.'
                          : 'Zipcode is required.'
                        : ''
                    }
                  />
                )}
              />
            </Item>
          </Grid>
          <Grid item xs={12}>
            <Item elevation={0}>
              <Controller
                name="relationshiptoPatient"
                control={control}
                rules={{ required: true }}
                render={({ field: { onChange, value } }) => (
                  <Dropdown
                    id="relationshiptoPatient"
                    value={value || ''}
                    label={
                      <span>
                        Relationship to patient <span className="red">*</span>
                      </span>
                    }
                    error={Boolean(errors?.relationshiptoPatient)}
                    helperText={errors?.relationshiptoPatient ? 'Relationship to patient is required.' : undefined}
                    selectOptions={[
                      { name: 'Parent', value: 'Parent' },
                      { name: 'Spouse', value: 'Spouse' },
                      { name: 'Daughter', value: 'Daughter' },
                      { name: 'Son', value: 'Son' },
                      { name: 'Legal guardian', value: 'Legal guardian' },
                      {
                        name: 'Durable power of attorney (DPOA)',
                        value: 'Durable power of attorney (DPOA)',
                      },
                      { name: 'Other', value: 'Other' },
                    ]}
                    onChange={(e) => onChange(e.target.value)}
                  />
                )}
              />
            </Item>
          </Grid>
          <Grid item xs={12}>
            <Item elevation={0}>
              <Button text="Continue" color="primary" type="submit" />
            </Item>
          </Grid>
        </form>
      </Grid>
    </Box>
  )
}
export default adultPageOne
